import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  financeApi,
  type RevenuePeriod,
  type TVAListParams,
  type MarginListParams,
  type ExpenseListParams,
  type CreateExpenseInput,
  type FinanceInvoiceListParams,
} from "@/lib/admin-api/finance";

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const KEYS = {
  revenue: (period: RevenuePeriod, dateFrom?: string, dateTo?: string) =>
    ["admin-finance-revenue", period, dateFrom, dateTo] as const,
  tva: (params: TVAListParams) => ["admin-finance-tva", params] as const,
  margins: (params: MarginListParams) =>
    ["admin-finance-margins", params] as const,
  expenses: (params: ExpenseListParams) =>
    ["admin-finance-expenses", params] as const,
  invoices: (params: FinanceInvoiceListParams) =>
    ["admin-finance-invoices", params] as const,
  cashflow: (dateFrom: string, dateTo: string) =>
    ["admin-finance-cashflow", dateFrom, dateTo] as const,
  sageExports: ["admin-finance-sage-exports"] as const,
  reports: ["admin-finance-reports"] as const,
  dailyPnl: (dateFrom: string, dateTo: string) =>
    ["admin-finance-daily-pnl", dateFrom, dateTo] as const,
} as const;

// ---------------------------------------------------------------------------
// Revenue
// ---------------------------------------------------------------------------

export function useRevenue(
  period: RevenuePeriod,
  dateFrom?: string,
  dateTo?: string,
) {
  return useQuery({
    queryKey: KEYS.revenue(period, dateFrom, dateTo),
    queryFn: () => financeApi.revenue.get(period, dateFrom, dateTo),
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// TVA
// ---------------------------------------------------------------------------

export function useTVAReport(params: TVAListParams = {}) {
  return useQuery({
    queryKey: KEYS.tva(params),
    queryFn: () => financeApi.tva.list(params),
    staleTime: 60_000,
  });
}

export function useSubmitTVA() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => financeApi.tva.submit(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-finance-tva"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Margins
// ---------------------------------------------------------------------------

export function useMargins(params: MarginListParams = {}) {
  return useQuery({
    queryKey: KEYS.margins(params),
    queryFn: () => financeApi.margins.list(params),
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Expenses
// ---------------------------------------------------------------------------

export function useExpenses(params: ExpenseListParams = {}) {
  return useQuery({
    queryKey: KEYS.expenses(params),
    queryFn: () => financeApi.expenses.list(params),
    staleTime: 30_000,
  });
}

export function useCreateExpense() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateExpenseInput) =>
      financeApi.expenses.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-finance-expenses"] });
    },
  });
}

export function useApproveExpense() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => financeApi.expenses.approve(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-finance-expenses"] });
    },
  });
}

export function useRejectExpense() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => financeApi.expenses.reject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-finance-expenses"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Finance Invoices
// ---------------------------------------------------------------------------

export function useFinanceInvoices(params: FinanceInvoiceListParams = {}) {
  return useQuery({
    queryKey: KEYS.invoices(params),
    queryFn: () => financeApi.invoices.list(params),
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Cashflow
// ---------------------------------------------------------------------------

export function useCashflow(dateFrom: string, dateTo: string) {
  return useQuery({
    queryKey: KEYS.cashflow(dateFrom, dateTo),
    queryFn: () => financeApi.cashflow.get(dateFrom, dateTo),
    staleTime: 60_000,
    enabled: Boolean(dateFrom && dateTo),
  });
}

// ---------------------------------------------------------------------------
// Sage Export
// ---------------------------------------------------------------------------

export function useSageExports() {
  return useQuery({
    queryKey: KEYS.sageExports,
    queryFn: () => financeApi.sage.list(),
    staleTime: 30_000,
  });
}

export function useTriggerSageExport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (period: string) => financeApi.sage.trigger(period),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.sageExports });
    },
  });
}

// ---------------------------------------------------------------------------
// Reports
// ---------------------------------------------------------------------------

export function useReports() {
  return useQuery({
    queryKey: KEYS.reports,
    queryFn: () => financeApi.reports.list(),
    staleTime: 60_000,
    retry: 1,
  });
}

export function useGenerateReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      templateId,
      period,
    }: {
      readonly templateId: string;
      readonly period: string;
    }) => financeApi.reports.generate(templateId, period),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.reports });
    },
  });
}

// ---------------------------------------------------------------------------
// Daily P&L
// ---------------------------------------------------------------------------

export function useDailyPnL(dateFrom: string, dateTo: string) {
  return useQuery({
    queryKey: KEYS.dailyPnl(dateFrom, dateTo),
    queryFn: () => financeApi.dailyPnl.get(dateFrom, dateTo),
    staleTime: 60_000,
    enabled: Boolean(dateFrom && dateTo),
  });
}
